import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";

interface ModalState {
  openModalComponentName: string | null;
  modalData: Record<string, any>;
  modals: Record<string, boolean>;
  modalDataById: Record<string, Record<string, unknown>>;
}

const initialState: ModalState = {
  openModalComponentName: null,
  modalData: {},
  modals: {},
  modalDataById: {},
};

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<{ name: string; data?: Record<string, any> }>) => {
      state.openModalComponentName = action.payload.name;
      state.modalData = action.payload.data || {};
    },
    closeModal: (state) => {
      state.openModalComponentName = null;
      state.modalData = {};
    },
    setModalOpen: (state, action: PayloadAction<{ modalId: string; isOpen: boolean; data?: Record<string, unknown> }>) => {
      state.modals[action.payload.modalId] = action.payload.isOpen;
      if (action.payload.data !== undefined) {
        state.modalDataById[action.payload.modalId] = action.payload.data;
      } else if (!action.payload.isOpen) {
        delete state.modalDataById[action.payload.modalId];
      }
    },
  },
});

export const { openModal, closeModal, setModalOpen } = modalSlice.actions;
export default modalSlice.reducer;

// Hook for accessing modal state (single modal pattern)
export function useModalState() {
  const openModalComponentName = useSelector(
    (state: any) => state.modal.openModalComponentName
  );
  const modalData = useSelector(
    (state: any) => state.modal.modalData
  );

  return {
    openModal: openModalComponentName,
    modalData,
  };
}

// Hook for managing individual modal states (multi-modal pattern)
export function useModal(modalId: string) {
  const dispatch = useDispatch();
  const isOpen = useSelector(
    (state: any) => state.modal.modals[modalId] ?? false
  );
  const modalData = useSelector(
    (state: any) => state.modal.modalDataById[modalId] as Record<string, unknown> | undefined
  );

  const open = useCallback((data?: Record<string, unknown>) => {
    dispatch(setModalOpen({ modalId, isOpen: true, data }));
  }, [dispatch, modalId]);

  const close = useCallback(() => {
    dispatch(setModalOpen({ modalId, isOpen: false }));
  }, [dispatch, modalId]);

  const toggle = useCallback(() => {
    dispatch(setModalOpen({ modalId, isOpen: !isOpen }));
  }, [dispatch, modalId, isOpen]);

  return {
    isOpen,
    open,
    close,
    toggle,
    modalData,
  };
}
